import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient, { ApiResponse } from "./apiClient";

export interface Offer {
  id: number;
  name: string;
  description: string;
  start_date: string;
  expiry_date: string;
  tags: number[];
  image_url: string;
  is_active: boolean;
  is_enabled: boolean;
  redeemable_only_once: boolean;
}

export interface OfferPayload {
  name: string;
  description: string;
  tags: number[];
  start_date: string;
  expiry_date: string;
  image_url: string;
  redeemable_only_once?: boolean;
  is_enabled?: boolean;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface PaginatedOffers {
  offers: Offer[];
  pagination: PaginationMeta;
}

const QUERY_KEY = ["offers"];

const fetchOffers = async (page: number): Promise<PaginatedOffers> => {
  const { data: response } = await apiClient.get<ApiResponse<{ offers: Offer[]; pagination: PaginationMeta }>>(
    "/v1/admin/offers",
    { params: { page } }
  );
  return response.data;
};

export const useOffers = (page: number = 1) =>
  useQuery({
    queryKey: [...QUERY_KEY, page],
    queryFn: () => fetchOffers(page),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });


const addOffer = async (body: OfferPayload): Promise<Offer> => {
  const { data: response } = await apiClient.post<ApiResponse<Offer>>("/v1/admin/offers", body);
  return response.data;
};


export const useAddOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addOffer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const updateOffer = async ({ id, body }: { id: number; body: OfferPayload }): Promise<Offer> => {
  const { data: response } = await apiClient.put<ApiResponse<Offer>>(`/v1/admin/offers/${id}`, body);
  return response.data;
};

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateOffer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};


const deleteOffer = async (id: number): Promise<void> => {
  await apiClient.delete(`/v1/admin/offers/${id}`);
};

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteOffer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};


// ── Offer Broadcasts ──────────────────────────────────────────────────────────

export type BroadcastStatus = "pending" | "processing" | "completed" | "failed" | "cancelled";

export interface OfferBroadcast {
  broadcast_id: number;
  broadcast_number: number;
  status: BroadcastStatus;
  source_type: string;
  source_id: number;
  total_recipients: number;
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  progress_percent: number;
  triggered_by_uid: string;
  created_at: string;
  started_at: string;
  completed_at: string;
  last_error: string;
}

const fetchOfferBroadcasts = async (offerId: number): Promise<OfferBroadcast[]> => {
  const { data: response } = await apiClient.get<ApiResponse<OfferBroadcast[]>>(
    `/v1/admin/offers/${offerId}/broadcasts`
  );
  return response.data;
};

export const useOfferBroadcasts = (offerId: number) =>
  useQuery({
    queryKey: ["offers", offerId, "broadcasts", "list"],
    queryFn: () => fetchOfferBroadcasts(offerId),
    enabled: !!offerId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

const fetchOfferBroadcast = async (offerId: number, broadcastId: number): Promise<OfferBroadcast> => {
  const { data: response } = await apiClient.get<ApiResponse<OfferBroadcast>>(
    `/v1/admin/offers/${offerId}/broadcasts/${broadcastId}`
  );
  return response.data;
};

export const useOfferBroadcast = (offerId: number, broadcastId: number, options?: { polling?: boolean }) =>
  useQuery({
    queryKey: ["offers", offerId, "broadcasts", broadcastId],
    queryFn: () => fetchOfferBroadcast(offerId, broadcastId),
    enabled: !!offerId && !!broadcastId,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchInterval: options?.polling ? 30000 : false,
  });

const triggerOfferBroadcast = async (offerId: number): Promise<OfferBroadcast> => {
  const { data: response } = await apiClient.post<ApiResponse<OfferBroadcast>>(
    `/v1/admin/offers/${offerId}/broadcasts`
  );
  return response.data;
};

export const useTriggerOfferBroadcast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerOfferBroadcast,
    onSuccess: (broadcast, offerId) =>
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "broadcasts", broadcast.broadcast_id] }),
  });
};

